import { calculateCustomFreightSingle } from '../../services/apis/fee/index'
import type { LogisticsOrderInfo } from '../order/createSlice/types'
import type { ExpressListData } from '../serviceProviderSlice/types'
import { CUSTOM_FREIGHT_REASON_ENUM, ENUM_TYPES } from './enum'
import type { IExpressData, IExpressProps, IProviderAddressVO, IProviderFormValues, IServiceProviderVO, ISetProviderProps } from './types'
import {
  calculateTotalPrice,
  convertAddress,
  formatProvinceUtil,
  getCodFee,
  getDeliveryTime,
  getExpressConfig,
  getPremium,
  getServiceProvider,
  getShipmentServiceType,
  handlePayment,
  handleSubAccount,
  initialPickupTimeWithChange,
  logger,
  shouldClearInsuranceAmountFn,
  sortProviderList,
} from './utils'
import { COD_TAB, SHIPMENT_PICKUP_TYPE } from '@/bizComponent/ServiceProvider/enum'
import type { ProviderType } from '@/constant'
import { INSURANCE_SUPPORT_TYPE, INSURANCE_TYPE, PaymentTypeEnum, SAAS_FLAG } from '@/constant'
import { fetchRateList } from '@/services/apis/common'
import i18n from '@/services/i18n'
import type { AppDispatch, AppGetState, AppThunk, AppThunkDispatch } from '@/store'
import { isCodCurrencyCodeEditable, isKerryHk2TWFn } from '@/utils'
import type { PayloadAction } from '@reduxjs/toolkit'
import { createSlice } from '@reduxjs/toolkit'
import { OneMessage } from '@yy/one-ui'
import type { FormInstance } from 'antd'
import { pick } from 'lodash'
import moment from 'moment-timezone'
import { divide, times } from 'number-precision'

const initialState: Partial<IServiceProviderVO> = {
  type: 'normal',
  tab: `${COD_TAB.ALL}`,
  filterArr: [],
  premiumLoading: false,
  codConvertLoading: false,
  codConvertResolved: true,
}

const configSlice = createSlice({
  name: 'newServiceProvider',
  initialState,
  reducers: {
    setProviderOptions(state, action: PayloadAction<IServiceProviderVO>) {
      return {
        ...state,
        ...action.payload,
      }
    },
    update(state, action: PayloadAction<Partial<IServiceProviderVO>>) {
      return {
        ...state,
        ...action.payload,
      }
    },
    /**
     * 更新依赖的 query
     * @param state
     * @param action
     * @returns
     */
    updatePropsAction(state, action: PayloadAction<IExpressProps>) {
      const props = action.payload
      return {
        ...state,
        props,
      }
    },
  },
})

export const { setProviderOptions, update, updatePropsAction } = configSlice.actions

export const setProps = (props: ISetProviderProps) => async (dispatch: AppDispatch, getState: AppGetState) => {
  const { newServiceProvider: state, dictionary } = getState()
  const { cover, type, ...payload } = props
  // 覆盖
  if (cover) {
    return {
      ...state,
      props: payload,
    }
  }

  const addressVO = {
    ...(state.props?.addressVO || {}),
    ...(payload?.addressVO || {}),
  }

  // update
  const updateProps = {
    ...(state.props || {}),
    ...payload,
    addressVO: formatProvinceUtil(addressVO),
  } as IExpressProps

  // 兼容字段
  if (props.arrival_method) {
    updateProps.supportedAddressTypes = [props.arrival_method == 1 ? 'HOME_STREET' : 'STORE_STREET']
  }
  // 第一次初始化，可能没有 currencyCode
  if (props?.addressVO?.arrival_countryCode && !state?.props?.currencyCode && !props?.currencyCode) {
    const { arrival_countryCode: arrivalCountry } = props?.addressVO
    const arrivalCurrencyCode = dictionary?.countryMapDics?.[arrivalCountry]?.currency
    if (arrivalCurrencyCode) {
      updateProps.currencyCode = arrivalCurrencyCode
    }
  }

  if (type) {
    await dispatch(
      update({
        type,
      })
    )
  }

  await dispatch(updatePropsAction(updateProps))

  /** 修改了收货国家 */
  if (payload?.addressVO?.arrival_countryCode) {
    dispatch(updateShippingInfo())
  }

  if (props.previousShipmentServiceType) {
    dispatch(
      update({
        previousShipmentServiceType: props.previousShipmentServiceType,
      })
    )
  }

  if (props.previousCodFee) {
    dispatch(
      update({
        previousCodFee: props.previousCodFee,
      })
    )
  }
}

const initConfig = {
  setStore: true,
  toast: false,
}

export const fetchProviderList = (config?: { setStore?: boolean; toast?: boolean }) => async (dispatch, getState: AppGetState) => {
  const { setStore = true, toast } = Object.assign({}, initConfig, config || {})

  const state = getState()
  const { user, newServiceProvider } = state
  const { isPrepaid } = user?.info ?? {}
  const senderCountryCode = user?.info?.countryCode
  const currencyCode = user?.info?.currencyCode
  setStore &&
    dispatch(
      update({
        listLoading: true,
      })
    )
  try {
    const { list, expressList } = await getServiceProvider({
      currencyCode,
      isPrepaid,
      props: {
        ...newServiceProvider.props,
        addressVO: {
          ...newServiceProvider?.props?.addressVO,
          departure_countryCode: senderCountryCode,
        },
        queryDeliveryDate: true,
      },
      type: newServiceProvider.type,
    })

    if (toast && !list?.length) {
      OneMessage.error(i18n.t('newOrder.noMatch'))
    }
    setStore &&
      dispatch(
        update({
          list,
          expressList,
        })
      )
    return list
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('get service provider list error', error)
    return []
  } finally {
    setStore &&
      dispatch(
        update({
          listLoading: false,
        })
      )
  }
}
export interface HandleOptionsData {
  provider: Partial<IExpressData>
  formValues?: IProviderFormValues
}

export const handleExtraConfig = (data: HandleOptionsData) => async (_: AppDispatch, getState: AppGetState) => {
  const { newServiceProvider, user } = getState()
  const { props } = newServiceProvider
  const senderCountryCode = user.info.countryCode

  const { provider, formValues } = data
  /**
   * 获取揽收时间和税号展示配置
   * pickupTime 是跟服务产品具体指挂钩的，因此这里需要考虑数据默认值跟服务产品默认值的情况，因此他的初始化Options是根据具体值来确定的，而不是无脑取服务产品的值
   */
  const config = await (async () => {
    const { supportedPickupTypes, expressTypeCode } = provider
    const expressConfig = await getExpressConfig({
      formValues,
      supportedPickupTypes,
      expressTypeCode,
      address: {
        senderCountryCode,
        consigneeCountryCode: props.addressVO?.arrival_countryCode,
      },
    })

    // 默认选中的税号类型
    const defaultTaxType = expressConfig?.extraConfig?.find((item) => item.key === 'customerTax')?.options?.[0]?.option

    return { ...expressConfig, defaultTaxType }
  })()

  return config
}

/**
 * 处理服务产品的 options
 * @description
 * 这里provider的来源可能是默认的服务产品信息，也有可能是用户在服务产品列表选择的信息
 */
export const handleOptions = (data: HandleOptionsData & { isInit?: boolean }) => async (dispatch: AppDispatch, getState: AppGetState) => {
  const { provider, formValues, isInit } = data
  const state = getState()
  const {
    newServiceProvider: { type },
    user: { walletList, info },
  } = state

  const senderCountryCode = info?.countryCode
  const { companyCode, settlementModes, estimatedCost, expressTypeCode, needSubAccount, saasFlag, supportedCheckoutTypes, freightCurrencyCode } = provider

  /** cod 选项 */
  const provider_payment_type = (() => {
    if (formValues?.provider_payment_type) {
      return formValues.provider_payment_type
    }

    const { type, filterArr, previousShipmentServiceType } = state.newServiceProvider

    const provider_payment_type = getShipmentServiceType({
      supportedCheckoutTypes,
      type,
      filterArr,
      previousShipmentServiceType,
    })

    return provider_payment_type
  })()
  /** 货到付款金额 */
  const provider_cod_amount = (() => {
    if (formValues?.provider_cod_amount) {
      return formValues.provider_cod_amount
    }

    const { type, previousCodFee, props } = state.newServiceProvider
    const orderCodAmount = props?.apiCodAmount
    const provider_cod_amount = getCodFee({
      provider_payment_type: provider_payment_type && Number(provider_payment_type),
      type,
      previousCodFee,
      orderCodAmount,
    })

    return provider_cod_amount
  })()

  /** 支付方式 */
  const paymentData = (() => {
    const isPrepaid = state?.user?.info?.isPrepaid
    const userCurrencyCodeList = walletList.map((wallet) => wallet.currencyCode)
    const freightCurrencyCodeNoInUserCurrencyCodeList = freightCurrencyCode && !userCurrencyCodeList.includes(freightCurrencyCode)

    const paymentData = handlePayment({
      companyCode,
      settlementModes,
      estimatedCost,
      isPrepaid,
      saasFlag,
      type,
      freightCurrencyCodeNoInUserCurrencyCodeList,
    })

    return paymentData
  })()

  const { dateData, extraConfig, defaultTaxType } = await dispatch(
    handleExtraConfig({
      formValues,
      provider,
    })
  )

  /** 预约派送时间 */
  const deliveryTimeData = getDeliveryTime({
    provider,
    formValues: pick(formValues, ['provider_delivery_date', 'provider_delivery_time']),
  })

  const isKerryHk2TW = (() => {
    const { addressVO } = state.newServiceProvider.props ?? {}
    if (addressVO && senderCountryCode && addressVO.arrival_countryCode) {
      return isKerryHk2TWFn({
        companyCode,
        senderCountryCode,
        consigneeCountryCode: addressVO.arrival_countryCode,
      })
    } else {
      logger('发货国家或收货国家没有初始化')
      return false
    }
  })()

  /** 子账号的问题同 getPickupTime */
  const subAccountData = await (async () => {
    if (needSubAccount) {
      const { list, value } = await handleSubAccount({
        expressTypeCode,
        pickupType: formValues?.provider_pickup_type ?? dateData.provider_pickup_type,
      })

      // 揽收方式为dropoff 到店寄件 时子账号返回空,toast配置账号
      if (!isInit && !list.length && dateData.provider_pickup_type === SHIPMENT_PICKUP_TYPE.dropOff) {
        OneMessage.error(i18n.t('system.error.LST-123677-B0012'))
      }

      return {
        list,
        defaultValue: value,
      }
    }

    return {
      list: [],
      defaultValue: '',
    }
  })()

  return {
    subAccountData,
    isKerryHk2TW,
    dateData,
    paymentData,
    provider_payment_type,
    provider_cod_amount,
    deliveryTimeData,
    extraConfig,
    defaultTaxType,
  }
}

export const handlePickupMethodChangeWithSubAccount =
  (pickupType: number): AppThunk =>
  async (dispatch, getState, form: FormInstance) => {
    const state = getState()
    const { needSubAccount, expressTypeCode } = state.newServiceProvider.provider ?? {}
    if (!needSubAccount) return

    const { list, value } = await handleSubAccount({
      expressTypeCode,
      pickupType,
    })

    // 揽收方式为dropoff 到店寄件 时子账号返回空,toast配置账号
    if (!list.length && pickupType === SHIPMENT_PICKUP_TYPE.dropOff) {
      OneMessage.error(i18n.t('system.error.LST-123677-B0012'))
    }

    dispatch(
      update({
        subAccountOption: list.map((item) => ({ value: item.accountNo, label: item.remark })),
      })
    )
    form?.setFieldsValue({
      provider_channel_seller_id: value,
    })
  }

/**
 * 处理选中服务产品
 * @returns
 */
export const handleSelectServiceProviderSlice =
  (provider: ExpressListData, inputForm?: FormInstance) => async (dispatch: AppDispatch, getState: AppGetState, form: FormInstance) => {
    const formInstance = inputForm ?? form
    const { newServiceProvider, user } = getState()
    const { previousProvider, type, props } = newServiceProvider
    const fieldsValue = formInstance.getFieldsValue()
    await dispatch(calculateGoodsPrice(formInstance, provider))
    // 处理 options
    const { dateData, paymentData, subAccountData, isKerryHk2TW, provider_payment_type, provider_cod_amount, deliveryTimeData, extraConfig, defaultTaxType } = await dispatch(
      handleOptions({
        provider,
      })
    )
    const finalProviderCodAmount = previousProvider ? form.getFieldValue('provider_cod_amount') : provider_cod_amount // 如果为切换服务产品，则provider保留原值

    dispatch(
      update({
        providerExtraConfig: extraConfig,
        providerDateOptions: dateData.shipmentPickupDateOptions,
        providerTimeOptions: dateData.shipmentPickupTimeRangOptions,
        providerDeliveryDateOptions: deliveryTimeData.providerDeliveryDateOptions,
        providerPaymentOptions: paymentData.options,
        subAccountOption: subAccountData.list?.map((item) => ({ value: item.accountNo, label: item.remark })),
        isKerryHk2TW,
        provider,
        previousShipmentServiceType: provider_payment_type,
        props: { ...newServiceProvider.props, isAutoMatch: false },
      })
    )

    const preInsuranceType = form.getFieldValue('provider_insurance_type') //  上一次的值
    const preInsuranceAmount = form.getFieldValue('provider_insured_amount') //  上一次的值
    const supportIns = provider.insuranceSupport === INSURANCE_SUPPORT_TYPE.YES // 当前的服务产品是否支持保险

    /**
     * 服务商为 GTHK 必须买保险
     * @see https://shopline.yuque.com/mlu41d/gy9ldi/dbmtq9
     */
    /** 是否默认购买保险 */
    const provider_insurance_type = (() => {
      if (supportIns) {
        if (provider.companyCode === ('GHTK' as ProviderType)) {
          return INSURANCE_TYPE.YES
        }

        return preInsuranceType
      }

      return INSURANCE_TYPE.NO
    })()
    const shouldClearInsuranceAmount = shouldClearInsuranceAmountFn({
      preInsuranceType,
      provider,
      previousProvider,
      defaultCurrencyCode: user.info.currencyCode,
      type,
    })

    const formValues = {
      provider_cod_amount: finalProviderCodAmount,
      provider_payment_type,
      provider_pay_type: paymentData.defaultValue,
      provider_pickup_type: dateData.provider_pickup_type,
      provider_pickup_date: dateData.provider_pickup_date,
      provider_pickup_time_slots: dateData.provider_pickup_time_slots,
      provider_channel_seller_id: subAccountData.defaultValue,
      provider_insurance_type,
      provider_insured_amount: shouldClearInsuranceAmount ? '' : preInsuranceAmount,
      provider_premium: '', // 需要清空保费，会重新计算
      provider_delivery_date: deliveryTimeData.provider_delivery_date,
      provider_delivery_time: deliveryTimeData.provider_delivery_time,
      provider_tax_type: defaultTaxType,
      provider_consignee_name: fieldsValue?.arrival_fullName,
    } as Record<string, string | number>
    if (!fieldsValue.provider_cod_currency_code) {
      // 如果表单中COD币种没有值，说明之前没有选择服务产品，此时需要初始化cod币种：
      // 1.如果订单原始币种与商品币种一致，说明没有修改过商品币种，此时初始化为订单原始COD币种
      // 2.如果订单原始币种与商品币种不一致，说明已经修改过商品币种，此时初始化为商品的币种
      Object.assign(formValues, {
        provider_cod_currency_code: props?.orderInfo?.orderAmountCurrencyCode === fieldsValue.parcel_currency_code ? props?.apiCodCurrencyCode : fieldsValue.parcel_currency_code,
      })
    }

    // 切换服务产品，cod币种处理：
    // 1.切换至特殊渠道（华磊万立德）：币种转换-目标币种需要使用服务产品配置的特定币种；非币种转换-cod币种需要使用服务产品配置的特定币种
    if (isCodCurrencyCodeEditable(provider?.expressTypeCode)) {
      const codCurrencyConverted = await dispatch(
        updateCodValue({
          codFee: finalProviderCodAmount,
          targetCurrencyCode: provider.supportedCurrencies[0],
          currencyCode: fieldsValue.provider_cod_currency_code || formValues.provider_cod_currency_code,
        })
      )
      if (!codCurrencyConverted) {
        Object.assign(formValues, {
          provider_cod_currency_code: provider?.supportedCurrencies?.[0],
        })
        if (formValues.provider_cod_currency_code !== fieldsValue.provider_cod_currency_code) {
          // 如果币种变了，又没有进行币种转换，则清空金额
          formValues.provider_cod_amount = null
        }
      }
      // 2.特殊渠道（华磊万立德）切换至普通渠道，cod币种需要还原为普通逻辑：币种转换-目标币种为收货国家币种；非币种转换-cod币种为商品币种
    } else if (isCodCurrencyCodeEditable(previousProvider?.expressTypeCode)) {
      const codCurrencyConverted = await dispatch(
        updateCodValue({
          codFee: finalProviderCodAmount,
          currencyCode: fieldsValue.parcel_currency_code, // 币种重置为商品币种
        })
      )
      // 非币种转换：从特殊渠道切换到非特殊渠道，币种重置为商品币种，金额清空
      if (!codCurrencyConverted) {
        Object.assign(formValues, {
          provider_cod_currency_code: fieldsValue.parcel_currency_code,
        })
        if (formValues.provider_cod_currency_code !== fieldsValue.provider_cod_currency_code) {
          // 如果币种变了，又没有进行币种转换，则清空金额
          formValues.provider_cod_amount = null
        }
      }
    } else {
      await dispatch(
        updateCodValue({
          codFee: finalProviderCodAmount,
          currencyCode: fieldsValue.parcel_currency_code,
        })
      )
    }
    formInstance?.setFieldsValue({
      ...formValues,
    })
    // 切换服务产品，会修改是否代收货款表单值（provider_payment_type），代收货款金额需要重新校验（避免代收货款金额disabled的情况下有些校验信息没有清除）
    formInstance.validateFields(['provider_cod_amount'])

    /**
     * 如果为 GTHK 则默认为购买保险, 这里需要重新计算商品价格, 包含自动转换货币逻辑
     * @see https://shopline.yuque.com/mlu41d/gy9ldi/dbmtq9
     */
    if (supportIns && provider.companyCode === ('GHTK' as ProviderType) && type !== 'batch') {
      if (formValues.provider_insurance_type === INSURANCE_TYPE.YES) {
        await dispatch(handleInsuranceTypeYes({ forceCalculate: true }))
      }
    }

    // 重新计算保费
    if (preInsuranceAmount && supportIns && preInsuranceType === INSURANCE_TYPE.YES) {
      await dispatch(handleCalculatePremium(preInsuranceAmount))
    }
    await dispatch(clearCustomFreight(formInstance))
    await dispatch(handleCalculateCustomFreight(formInstance))
  }

/**
 * 根据 expressCode 初始化服务产品
 * 不传 form 的话，可能拿到的 form 实例不是同一个
 * @param expressCode
 * @returns
 */
export const getProviderWithExpressCode =
  (expressCode: string) =>
  async (dispatch: AppThunkDispatch): Promise<{ provider?: ExpressListData; isSame: boolean }> => {
    const list = await dispatch(
      fetchProviderList({
        setStore: false,
      })
    )

    const sortedList = sortProviderList(list, {
      filter: true,
    })

    const index = sortedList.findIndex((item) => item.expressTypeCode === expressCode)
    const provider = index !== -1 ? sortedList[index] : sortedList?.[0]
    return {
      provider: {
        ...provider,
      },
      isSame: index !== -1,
    }
  }

export interface InitProviderAndForm {
  provider: Partial<IExpressData>
  formValues?: IProviderFormValues
  props?: IExpressProps
}

/**
 * 从订单详情初始化
 * @description
 * *注意:*
 * 编辑场景, 表单详情是以服务产品信息为基础, 部分修改了服务产品选项
 * 如果只走服务产品初始化逻辑, 会导致后续逻辑错误
 * 例如下面 handleOptions 函数中 getPickupTime
 * 所需要的 supportedPickupTypes 为 provider 基础信息,
 * 实际上订单详情信息已经修改过服务产品的表单选项,
 * 所以会导致初始化值跟 options 无法对上;
 * 所以函数中的操作均以 `formValues` 的值优先级最高
 * 只会设置name为 provider_ 开头的表单值
 */
export const initProviderAndForm = (payload: InitProviderAndForm) => async (dispatch: AppThunkDispatch, _getState: AppGetState, form: FormInstance) => {
  const { provider, formValues, props } = payload
  // 初始化，先重置cod币种转换相关数据
  dispatch(
    update({
      value: {
        codCurrencyRate: undefined,
        codFetchTime: undefined,
        codCurrencyConverted: false,
      },
      codConvertResolved: true,
    })
  )
  // 先 set props
  if (props) {
    await dispatch(setProps(props))
  }
  // 如果 Provider 为空，不初始化后面
  if (!provider || !provider.companyCode || !provider?.expressTypeCode) {
    return
  }

  // 处理 options
  /** 这里如果是用provider的话需要考虑是初始化的场景还是选择服务产品初始化的场景 */
  const { dateData, paymentData, subAccountData, isKerryHk2TW, provider_cod_amount, provider_payment_type, deliveryTimeData, extraConfig, defaultTaxType } = await dispatch(
    handleOptions({ provider, formValues, isInit: true })
  )

  dispatch(
    update({
      providerExtraConfig: extraConfig,
      providerDateOptions: dateData.shipmentPickupDateOptions,
      providerTimeOptions: dateData.shipmentPickupTimeRangOptions,
      providerDeliveryDateOptions: deliveryTimeData.providerDeliveryDateOptions,
      providerDeliveryTimeOptions: deliveryTimeData.providerDeliveryTimeOptions,
      providerPaymentOptions: paymentData.options,
      subAccountOption: subAccountData.list?.map((item) => ({ value: item.accountNo, label: item.remark })),
      isKerryHk2TW,
      previousCodFee: props?.type !== 'merge' ? formValues?.provider_cod_amount || undefined : undefined,
      previousShipmentServiceType: formValues?.provider_payment_type || undefined,
      provider,
    })
  )

  const updateFormValues: Record<string, any> = {
    provider_cod_amount,
    provider_cod_currency_code: formValues?.provider_cod_currency_code,
    provider_payment_type,
    provider_pay_type: paymentData.defaultValue,
    provider_pickup_type: dateData.provider_pickup_type,
    provider_pickup_date: dateData.provider_pickup_date,
    provider_pickup_time_slots: dateData.provider_pickup_time_slots,
    provider_channel_seller_id: subAccountData.defaultValue,
    provider_tax_type: defaultTaxType,
  }

  // 如果有传入值，优先使用 form 的值
  /** 这里会存在一个问题，会用订单给的formValues覆盖服务产品的默认信息，但是options的又是用服务产品默认信息获取的  */
  if (formValues) {
    Object.assign(updateFormValues, {
      ...formValues,
      provider_channel_seller_id: formValues.provider_channel_seller_id || subAccountData.defaultValue,
      provider_pickup_type: formValues.provider_pickup_type ?? dateData.provider_pickup_type,
      provider_invoice_date: formValues.provider_invoice_date ? moment(formValues.provider_invoice_date) : undefined,
    })
  }

  Object.assign(updateFormValues, {
    provider_pay_type: paymentData.list.includes(updateFormValues.provider_pay_type) ? updateFormValues.provider_pay_type : paymentData.defaultValue,
  })

  Object.assign(updateFormValues, {
    provider_delivery_time: deliveryTimeData.provider_delivery_time,
    provider_delivery_date: deliveryTimeData.provider_delivery_date,
  })

  const finalFormValues = pick(
    updateFormValues,
    Object.keys(updateFormValues).filter((key) => key.startsWith('provider_'))
  )

  /** 部分签收 */
  if (typeof updateFormValues?.support_part_sign === 'string') {
    finalFormValues.support_part_sign = updateFormValues.support_part_sign
  }
  finalFormValues.supported_ATL = updateFormValues.supported_ATL
  finalFormValues.provider_supported_sod = updateFormValues.provider_supported_sod
  finalFormValues.provider_supported_safe_place = updateFormValues.provider_supported_safe_place
  const codCurrencyConverted = await dispatch(
    updateCodValue({
      codFee: provider_cod_amount,
      currencyCode: formValues?.provider_cod_currency_code,
      // 特殊渠道，转换后币种需要传配置的特定币种种的第一个，非特殊渠道，则不传，不传会默认去收货国家币种
      targetCurrencyCode: isCodCurrencyCodeEditable(provider?.expressTypeCode) ? provider.supportedCurrencies?.[0] : undefined,
    })
  )
  // 华磊万立德，非币种转换的情况下，cod币种为默认为服务产品支持币种的第一个，且金额清空：
  if (!codCurrencyConverted && isCodCurrencyCodeEditable(provider?.expressTypeCode) && provider.supportedCurrencies?.length) {
    // 默认选中第一个
    Object.assign(finalFormValues, {
      provider_cod_currency_code: provider?.supportedCurrencies?.[0],
    })
    // 重置COD币种后，金额也重置
    Object.assign(finalFormValues, {
      provider_cod_amount: '',
    })
  }
  form?.setFieldsValue(finalFormValues)
}

const clearCustomFreight =
  (inputForm: FormInstance): AppThunk =>
  async (dispatch) => {
    await dispatch(
      setProps({
        osFreightErrorCode: undefined,
      })
    )

    inputForm.setFieldsValue({
      provider_custom_freight_currency_code: undefined,
      provider_custom_freight: undefined,
    })
  }

/**
 *
 * @param inputForm
 * @returns
 */
export const handleCalculateCustomFreight =
  (inputForm?: FormInstance): AppThunk =>
  async (dispatch, getState: AppGetState, form: FormInstance) => {
    const state = getState()
    const { props, type, provider, value: codConvertedValue } = state.newServiceProvider

    const finalForm = inputForm || form

    const values = finalForm?.getFieldsValue()

    const isCod = values.provider_payment_type === PaymentTypeEnum.cod
    const isLogisticEdit = type === 'logistic-edit'

    /** logistics page or not the cod option don't has this function */
    if (isLogisticEdit || !isCod) {
      return
    }

    const addresses = convertAddress({
      addressVO: {
        ...props?.addressVO,
        departure_countryCode: values.departure_countryCode,
      },
      isHome: props?.supportedAddressTypes?.[0] === 'HOME_STREET',
      type: type,
    })

    const customFreightRes = await calculateCustomFreightSingle({
      senderAddress: addresses.senderAddressName,
      consigneeAddress: addresses.consigneeAddressName,
      expressTypeCode: provider.expressTypeCode,
      width: props?.parcel_width as string,
      height: props?.parcel_height as string,
      length: props?.parcel_length as string,
      weightUnit: props?.parcel_weight_unit,
      weight: props?.parcel_weight,
    })

    const { orderInfo: originOrderInfo } = props

    /**
     * 设置自定义运费错误
     */
    const setCustomFreightError = async (error: string) => {
      // price error with the OneShip custom freight calculation
      type IFreightErrorType = `${CUSTOM_FREIGHT_REASON_ENUM}`
      await dispatch(
        setProps({
          osFreightErrorCode: error as IFreightErrorType,
        })
      )

      finalForm.setFieldsValue({
        provider_custom_freight_currency_code: undefined,
        provider_custom_freight: undefined,
      })
    }

    const setCustomFreight = async () => {
      await dispatch(
        setProps({
          osFreightErrorCode: undefined,
        })
      )

      finalForm.setFieldsValue({
        provider_custom_freight_currency_code: customFreightRes.currencyCode,
        provider_custom_freight: divide(customFreightRes.freight, 100),
      })
    }

    // ======================           =================
    // ====================== 非 API 单 =================
    // ======================          =================
    if (!originOrderInfo?.isECommerce) {
      // 没有有错误的情况
      if (!customFreightRes.errorCode) {
        // 设置自定义运费
        setCustomFreight()
      } else {
        // 设置错误
        setCustomFreightError(customFreightRes.errorCode)
      }

      return
    }

    // ======================        =================
    // ====================== API 单 =================
    // ======================        =================
    const restoreCodAmount = () => {
      if (!originOrderInfo?.salesOrderAmount) return

      const provider_cod_amount = divide(originOrderInfo?.salesOrderAmount, 100)

      finalForm.setFieldsValue({
        provider_cod_amount,
      })

      return provider_cod_amount
    }
    /**
     * 币种转换
     * @description 出现在修改了 cod金额被修改的情况下需要重新计算 cod 转换金额
     * @param new_provider_cod_amount
     */
    const converteFunction = (new_provider_cod_amount) => {
      if (codConvertedValue.codCurrencyConverted && new_provider_cod_amount !== values.provider_cod_amount) {
        dispatch(
          updateCodValue({
            // 如果为非cod服务产品，provider_cod_amount为空，所以这里不能取’provider_cod_amount‘
            codFee: new_provider_cod_amount,
            currencyCode: values.provider_cod_currency_code,
          })
        )
      }
    }

    const isNumber = (value: unknown) => typeof value === 'number'

    const setFreightCurrencyCodeErrorOrCustomFreight = () => {
      // 有原本运费，有原本币种，且 cod 币种与运费币种不一致报错
      const isError = originOrderInfo?.salesOrderFreight && originOrderInfo?.salesOrderCurrencyCode && originOrderInfo?.salesOrderCurrencyCode !== values.provider_cod_currency_code
      // 原订单币种与现有 cod 币种不一致
      if (isError) {
        setCustomFreightError(CUSTOM_FREIGHT_REASON_ENUM.CAN_NOT_USE)
      } else {
        setCustomFreight()
      }
      return isError
    }

    // 是否可以触发 cod 恢复
    const canTriggerRecovery =
      originOrderInfo?.salesOrderAmount &&
      isNumber(originOrderInfo?.salesOrderFreight) &&
      // 是否扣减运费，记录是否修改过，修改会置空
      props?.deductedFreight &&
      // 原订单币种是否与现有cod币种一致
      originOrderInfo?.salesOrderCurrencyCode === values.provider_cod_currency_code

    // no error code, normal logic
    if (!customFreightRes.errorCode) {
      // 兼容华磊非币种转换不执行下面恢复 cod amount 的场景
      if (!values?.provider_cod_amount) {
        // 原订单币种与现有 cod 币种不一致
        return setFreightCurrencyCodeErrorOrCustomFreight()
      }

      // 原订单币种与现有 cod 币种不一致
      if (setFreightCurrencyCodeErrorOrCustomFreight()) {
        return
      }

      const isCurrencyConverted = !!codConvertedValue?.codCurrencyConverted

      // 判断是否能累加自定义运费
      // 如果不能，则执行还原回 orderAmount
      // 如果能，执行减去原订单运费
      const finalCodCurrencyCode = isCurrencyConverted ? values.provider_cod_currency_target : values.provider_cod_currency_code
      // 是否跟自定义运费币种一致
      const isSelfdefineCurrencySameWithFinalCurrency = customFreightRes.currencyCode === finalCodCurrencyCode

      // 不满足执行逻辑
      if (!canTriggerRecovery) {
        return
      }

      // 算出来，币种不一致的情况，需要还原回去原始订单 orderAmount
      if (!isSelfdefineCurrencySameWithFinalCurrency) {
        const newCodAmount = restoreCodAmount()
        return converteFunction(newCodAmount)
      }

      /** 没改过 cod，且重新支持了自定义运费，需要恢复减去运费的值 */
      if (isSelfdefineCurrencySameWithFinalCurrency) {
        // 设置自定义运费
        const provider_cod_amount = divide(originOrderInfo?.salesOrderAmount - originOrderInfo?.salesOrderFreight, 100)

        finalForm.setFieldsValue({
          provider_cod_amount,
        })

        converteFunction(provider_cod_amount)
      }

      return
    }

    /** 错误也要恢复值 */
    /** 华磊非币种转换 clear 掉 provider_cod_amount 的情况不走这里 */
    if (canTriggerRecovery && values?.provider_cod_amount) {
      const newCodAmount = restoreCodAmount()
      converteFunction(newCodAmount)
    }

    setCustomFreightError(customFreightRes.errorCode)
  }

/**
 * 修改揽收方式
 * @param provider_pickup_type
 * @returns
 */
export const handleShipmentPickupTypeChange =
  (provider_pickup_type: number): AppThunk =>
  async (dispatch, getState, form: FormInstance) => {
    const state = getState()
    const providerDateOptions = state.newServiceProvider.providerDateOptions
    const { providerTimeOptions, ...formValues } = initialPickupTimeWithChange(provider_pickup_type, {
      providerDateOptions,
    })

    dispatch(
      update({
        providerTimeOptions,
      })
    )

    form.setFieldsValue(formValues)
    dispatch(handleShipmentDateChange(formValues.provider_pickup_date))
  }

export const handleKeepPreviousCodFee =
  (codFee): AppThunk =>
  (dispatch) => {
    dispatch(
      update({
        previousCodFee: codFee,
      })
    )
  }

/** 监听 cod 选项 */
export const handleShipmentServerType =
  (provider_payment_type: number): AppThunk =>
  async (dispatch, getState, form: FormInstance) => {
    const { newServiceProvider } = getState()

    /** 因为第一次没有值，所以没有初始化 */
    const { previousCodFee, previousCodFeeTarget } = newServiceProvider
    if (provider_payment_type === 1) {
      form.setFieldsValue({
        provider_cod_amount: previousCodFee,
        provider_cod_amount_target: previousCodFee ? previousCodFeeTarget : 0,
      })
      await dispatch(handleCalculateCustomFreight(form))
    }

    dispatch(
      update({
        previousShipmentServiceType: provider_payment_type,
      })
    )
  }

interface HandleInsuranceTypeYesOptions {
  forceCalculate?: boolean
  orderInfo?: LogisticsOrderInfo
}

/**
 * 监听选择购买保险服务
 * @returns
 */
export const handleInsuranceTypeYes =
  (options?: HandleInsuranceTypeYesOptions): AppThunk =>
  async (dispatch, getState, form: FormInstance) => {
    const { forceCalculate = false, orderInfo } = options || {}
    const state = getState()
    const { isPrepaid, currencyCode } = state.user.info
    const provider = state.newServiceProvider.provider
    const supportIns = provider.insuranceSupport === INSURANCE_SUPPORT_TYPE.YES // 当前的服务产品是否支持保险

    if (isPrepaid && supportIns && !provider.insuranceSetting && provider.saasFlag !== SAAS_FLAG.YES) {
      // 预付用户，没配保费模板提示
      OneMessage.info(i18n.t('insurance.setting.info'))
    }

    // 运费币种（也是包裹价值币种）
    const freightCurrencyCode = provider.freightCurrencyCode || currencyCode
    if (provider.saasFlag !== SAAS_FLAG.YES) {
      if (orderInfo && freightCurrencyCode !== orderInfo.feeInfo.freightCurrencyCode) {
        // 包裹价值币种不等于原包裹价值币种，需要清除包裹价值和保费
        form.setFieldsValue({
          provider_insured_amount: '',
          provider_premium: '',
        })
        return
      }
    }

    // 包裹价值，优先用后端的数据
    if (forceCalculate !== true && form.getFieldValue('provider_insured_amount')) {
      dispatch(handleCalculatePremium(form.getFieldValue('provider_insured_amount')))
      return
    }

    const hasCurrencyRate = state.common.setting.CURRENCY_RATE.dataValue === '1'
    const maxInsuredAmount = state.newServiceProvider.provider.insuranceSetting?.maxInsuredAmount

    const { parcel_commodities, parcel_currency_code } = form.getFieldsValue()

    const totalPrice = await calculateTotalPrice({
      parcel_commodities,
      currencyCode: parcel_currency_code,
      freightCurrencyCode,
      hasCurrencyRate,
      maxInsuredAmount,
    })

    form.setFieldsValue({
      provider_insured_amount: totalPrice,
    })

    dispatch(handleCalculatePremium(totalPrice))
  }

/**
 * 计算保费
 * @param provider_insured_amount
 * @returns
 */
export const handleCalculatePremium =
  (provider_insured_amount: string | number): AppThunk =>
  async (dispatch, getState, form: FormInstance) => {
    if (!provider_insured_amount) {
      return
    }

    await form.validateFields(['provider_insured_amount'])

    const state = getState()
    const { freightCurrencyCode } = state.newServiceProvider.provider
    const { countryCode: senderCountryCode } = state.user.info || {}

    const { addressVO: inputAddressVO, supportedAddressTypes, parcel_weight, parcel_weight_unit, parcel_height, parcel_length, parcel_width } = state.newServiceProvider.props
    const { type } = state.newServiceProvider
    const { expressTypeCode, insuranceSetting } = state.newServiceProvider.provider

    if (!insuranceSetting) {
      // 没配置保费模板，不计算保费
      form.setFieldsValue({
        provider_premium: '',
      })
      return
    }

    const isHome = supportedAddressTypes?.[0] === 'HOME_STREET'

    const addressVO = convertAddress({
      addressVO: {
        ...inputAddressVO,
        departure_countryCode: senderCountryCode,
      },
      type,
      isHome,
    })

    const params = [
      {
        expressTypeCode,
        metadatas: [
          {
            weightUnit: parcel_weight_unit || 'kg',
            weight: Number(parcel_weight),
            length: Number(parcel_length),
            width: Number(parcel_width),
            height: Number(parcel_height),
            lengthUnit: 'cm',
            insuredAmount: times(Number(provider_insured_amount), 100),
            calculatePremium: true,
            calculateFreight: false,
            currencyCode: freightCurrencyCode, // 保费币种跟着运费币种
            metadataId: Math.floor(Math.random() * Math.pow(10, 20)) + '',
            senderAddress: addressVO?.senderAddressName,
            consigneeAddress: addressVO?.consigneeAddressName,
          },
        ],
      },
    ]

    dispatch(
      update({
        premiumLoading: true,
      })
    )

    try {
      const premium = await getPremium(params)
      form.setFieldsValue({
        provider_premium: typeof premium === 'number' ? divide(premium, 100) : '',
      })
    } catch (error) {}

    dispatch(
      update({
        premiumLoading: false,
      })
    )
  }

/**
 * 监听揽收日期
 * @param provider_pickup_date
 * @returns
 */
export const handleShipmentDateChange =
  (provider_pickup_date: string): AppThunk =>
  (dispatch, getState, form: FormInstance) => {
    const state = getState()
    const timeOptions = state.newServiceProvider.providerDateOptions?.find((item) => item.date === provider_pickup_date)?.timeIntervals ?? []
    const provider_pickup_time_slots = timeOptions?.[0]

    dispatch(
      update({
        providerTimeOptions: timeOptions,
      })
    )

    form?.setFieldsValue({
      provider_pickup_time_slots,
    })
  }

/**
 * 更新地址数据
 * @param payload
 * @returns
 */
export const updateAddressVO = (payload: IProviderAddressVO) => (dispatch, getState: AppGetState) => {
  const { newServiceProvider } = getState()
  const { props } = newServiceProvider

  const addressVO = {
    ...(props?.addressVO || {}),
    ...payload,
  }

  dispatch(
    updatePropsAction({
      ...props,
      addressVO: formatProvinceUtil(addressVO),
    })
  )
}

/**
 * 清除现有服务产品
 * @returns
 */
export const clearProvider = (): AppThunk => (dispatch, getState, form: FormInstance) => {
  dispatch(
    update({
      list: [],
      type: ENUM_TYPES.NORMAL,
      props: {},
      previousCodFee: undefined,
      previousShipmentServiceType: undefined,
      providerDeliveryDateOptions: [],
      providerDeliveryTimeOptions: [],
    })
  )

  dispatch(clearSelectedProvider({ inputForm: form }))
}

/**
 * 清除现有币种转换数据
 * @returns
 */
export const clearCodValue = (): AppThunk => (dispatch) => {
  dispatch(
    update({
      value: {
        codCurrencyRate: undefined,
        codFetchTime: undefined,
        codCurrencyConverted: false,
      },
      codConvertResolved: true,
    })
  )
}

/**
 * 清除现有服务产品和列表
 * @returns
 */
export const clearSelectedProvider = (data: { inputForm?: FormInstance; clearForm?: boolean }) => (dispatch, getState, form: FormInstance) => {
  const { inputForm, clearForm = true } = data
  dispatch(
    update({
      provider: null,
      providerDateOptions: [],
      providerTimeOptions: [],
      providerPaymentOptions: [],
      tab: 0,
      filterArr: [],
      previousCodFee: undefined,
      previousShipmentServiceType: undefined,
      previousProvider: undefined,
    })
  )

  // 不需要清除表单的情况
  if (!clearForm) {
    return
  }
  /**
   * 优先使用外面的
   */
  ;(inputForm || form)?.setFieldsValue({
    provider_payment_type: undefined,
    provider_cod_amount: undefined,

    /**
     *  发货方式
     */
    provider_pickup_type: undefined,

    /**
     * 预约揽收日期
     */
    provider_pickup_date: undefined,

    /**
     * 预约揽收时间
     */
    provider_pickup_time_slots: undefined,

    /**
     * 支付方式
     */
    provider_pay_type: undefined,

    /**
     * 月结账号
     */
    provider_monthly_account: undefined,

    /**
     * 月结子账号
     */
    provider_channel_seller_id: undefined,

    /**
     * 证件类型
     */
    provider_consignee_id_type: undefined,

    /**
     * 证件号
     */
    provider_consignee_id_num: undefined,

    /**
     * 税号类型
     */
    provider_tax_type: undefined,

    /**
     * 税号
     */
    provider_tax_no: undefined,

    /**
     * EORI号码
     */
    provider_custom_id_no: undefined,

    /**
     * 收件人姓名
     */
    provider_consignee_name: undefined,

    provider_invoice_date: undefined,
    provider_package_path: undefined,
    provider_invoice_no: undefined,
    provider_package_desc: undefined,

    provider_custom_freight: undefined,
    provider_custom_freight_currency_code: undefined,
  })
}

/**
 * 是否需要清除服务产品
 * @returns
 */
export const shouldClearProvider = (params?: { shouldShowError?: boolean }) => async (dispatch, getState: AppGetState, form: FormInstance) => {
  const { shouldShowError = true } = params || {}

  const { newServiceProvider } = getState()

  const { provider } = newServiceProvider

  const list: IExpressData[] = await dispatch(
    fetchProviderList({
      setStore: true,
      toast: shouldShowError,
    })
  )

  const sortedList = sortProviderList(list, {
    filter: true,
  })

  /**
   * if there has not any selected provider no need to update the following logic
   */
  if (!provider) {
    return
  }

  const updateProvider = sortedList.find((item) => item?.expressTypeCode === provider?.expressTypeCode)

  // 是否需要清除选中的 provider
  if (!updateProvider) {
    // 这里是第三步的时候如果服务产品不再支持的情况下，不清除 form
    dispatch(clearSelectedProvider({ clearForm: false }))
    // 如果不清除就更新一下服务产品
  } else {
    // 更新服务产品
    dispatch(
      update({
        provider: {
          ...(provider || {}),
          ...updateProvider,
        },
      })
    )
    // 更新支付方式选项
    dispatch(handlePayTypeOptions())

    //  update extra config
    const formValues = form.getFieldsValue()

    const configData = await dispatch(
      handleExtraConfig({
        formValues,
        provider,
      })
    )

    dispatch(
      update({
        providerExtraConfig: configData.extraConfig,
        providerDateOptions: configData?.dateData?.shipmentPickupDateOptions,
        providerTimeOptions: configData?.dateData?.shipmentPickupTimeRangOptions,
      })
    )

    const updateFormValues = {
      provider_pickup_type: configData?.dateData.provider_pickup_type,
      provider_pickup_date: configData?.dateData.provider_pickup_date,
      provider_pickup_time_slots: configData?.dateData?.provider_pickup_time_slots,
      provider_tax_type: configData?.defaultTaxType,
    }

    form.setFieldsValue(updateFormValues)
    /** recalculate the custom freight */
    await dispatch(handleCalculateCustomFreight(form))
  }
}

/** 支付方式选项
 * 根据服务产品运费更新付款方式options
 * 用户币种钱包没有此服务产品运费币种，只支持【到付现结】2(因为到付现结是线下给运费的，不扣钱包钱)
 * 算不出运费，也只支持【到付现结】
 */
export const handlePayTypeOptions = (): AppThunk => async (dispatch, getState) => {
  const { user, newServiceProvider } = getState()
  const { provider, type } = newServiceProvider

  if (provider) {
    const walletList = user.walletList
    const isPrepaid = user.info.isPrepaid
    const { freightCurrencyCode, saasFlag, estimatedCost = 0, companyCode, settlementModes } = provider
    const userCurrencyCodeList = walletList.map((wallet) => wallet.currencyCode)
    const freightCurrencyCodeNoInUserCurrencyCodeList = freightCurrencyCode && !userCurrencyCodeList.includes(freightCurrencyCode)

    const { options } = handlePayment({
      companyCode,
      settlementModes,
      estimatedCost,
      isPrepaid,
      saasFlag,
      type,
      freightCurrencyCodeNoInUserCurrencyCodeList,
    })

    dispatch(
      update({
        providerPaymentOptions: options,
      })
    )
  }
}

/**
 * 更新临时存储表单值
 * @param updateValue
 * @returns
 */
export const updateValue = (updateValue?: IServiceProviderVO['value']) => async (dispatch) => {
  dispatch(
    update({
      value: updateValue,
    })
  )
}

export const updateCodValue =
  (data: { codFee: number | string; targetCurrencyCode?: string; currencyCode?: string; arrivalCountry?: string }) =>
  async (dispatch, getState: AppGetState, form: FormInstance) => {
    const state = getState()
    const isConvertedScene = state.parcelSlice?.props?.isConvertedScene

    /** 用户设置的币种开关关闭，或者不是币种转换的场景（手工单创建，手工单编辑，运单编辑）不进行币种转换 */
    if (!isConvertedScene) return false

    const { countryMapDics } = state.dictionary
    const { provider } = state.newServiceProvider
    const { codFee, currencyCode, arrivalCountry, targetCurrencyCode } = data
    const supportedCurrency = isCodCurrencyCodeEditable(provider?.expressTypeCode) ? provider.supportedCurrencies?.[0] : undefined
    // 目的地国家取值优先级：传入的目的地国家 > 表单中收件国家
    const finalArrivalCountry = arrivalCountry || form.getFieldValue('arrival_countryCode')
    // 转换前币种取值优先级：传入的转换前币种 > 表单中cod原始币种
    const finalCurrencyCode = currencyCode || form.getFieldValue('provider_cod_currency_code')
    // 转换后币种取值优先级：传入的转换后币种 > 特殊渠道的话，取服务产品支持的币种中的第一个 > 目的地国家对应的币种
    const finalTargetCurrencyCode = targetCurrencyCode || supportedCurrency || countryMapDics?.[finalArrivalCountry]?.currency
    // cod币种与目的地国家币种或者（服务产品支持的币种）不一致，且用户开启币种转换，才进行币种转换
    if (!finalCurrencyCode || !finalTargetCurrencyCode || finalCurrencyCode === finalTargetCurrencyCode) {
      // 不需要币种转换的情况，需要重置store里面币种转换相关的数据
      dispatch(
        update({
          value: {
            codCurrencyRate: undefined,
            codFetchTime: undefined,
            codCurrencyConverted: false,
          },
        })
      )
      return false
    }

    const params = {
      sourceAmountList: [times(Number(codFee), 100)],
      sourceCurrency: finalCurrencyCode,
      targetCurrency: finalTargetCurrencyCode,
    }

    dispatch(
      update({
        codConvertLoading: true,
        codConvertResolved: false,
      })
    )

    try {
      const res = await fetchRateList(params)
      if (res.bizCode === 'SUCCESS') {
        const { targetAmountList, currencyRate, fetchTime } = res?.data
        const shipmentCodFeeTarget = divide(Number(targetAmountList?.[0]), 100)
        dispatch(
          update({
            value: {
              codCurrencyRate: currencyRate,
              codFetchTime: fetchTime,
              codCurrencyConverted: true,
            },
            previousCodFeeTarget: shipmentCodFeeTarget,
            codConvertResolved: true,
          })
        )
        form.setFieldsValue({
          provider_cod_currency_code: finalCurrencyCode,
          provider_cod_currency_target: finalTargetCurrencyCode,
          provider_cod_amount_target: shipmentCodFeeTarget,
        })
      }
    } catch (error) {}

    dispatch(
      update({
        codConvertLoading: false,
      })
    )

    return true
  }

export const updateProviderDeliveryTimeOptions = (provider_delivery_date) => (dispatch: AppDispatch, getState: AppGetState, form: FormInstance) => {
  const { providerDeliveryDateOptions } = getState().newServiceProvider
  const providerDeliveryTimeOptions = providerDeliveryDateOptions.find((item) => item.date === provider_delivery_date)?.timeIntervals ?? []
  dispatch(update({ providerDeliveryTimeOptions }))
  form.setFieldsValue({ provider_delivery_time: providerDeliveryTimeOptions?.[0] })
}

export const updateShippingInfo = () => async (dispatch: AppDispatch, getState: AppGetState) => {
  const state = getState()
  const { newServiceProvider, user } = state

  const { countryCode: senderCountryCode } = user.info ?? {}
  const companyCode = newServiceProvider.provider?.companyCode

  const isKerryHk2TW = (() => {
    const { addressVO } = state.newServiceProvider.props ?? {}
    if (addressVO && senderCountryCode && addressVO.arrival_countryCode) {
      return isKerryHk2TWFn({
        companyCode,
        senderCountryCode,
        consigneeCountryCode: addressVO.arrival_countryCode,
      })
    } else {
      logger('发货国家或收货国家没有初始化')
      return false
    }
  })()

  dispatch(
    update({
      isKerryHk2TW,
    })
  )
}

export const calculateGoodsPrice = (form: FormInstance, provider: ExpressListData) => async (dispatch: AppDispatch, getState: AppGetState) => {
  const { newServiceProvider } = getState()
  const { parcel_commodities, currencyCode } = form.getFieldsValue()

  const { type, previousCodFee, props } = newServiceProvider
  const { codCurrencyCode } = props || {}

  const finalCodCurrencyCode = isCodCurrencyCodeEditable(provider?.expressTypeCode) ? provider?.supportedCurrencies?.[0] : codCurrencyCode

  if (type === 'create' && !previousCodFee && currencyCode === finalCodCurrencyCode) {
    const total =
      parcel_commodities?.reduce((acc, next) => {
        return (acc += times(next.parcel_goods_price, next.parcel_goods_quantity || 1))
      }, 0) || ''

    if (typeof total !== 'number') {
      return
    }

    await dispatch(
      update({
        previousCodFee: total,
      })
    )
  }
}

/**
 * get current currency and codAmount
 */
export const getCurrentCodValue = (inputForm?: FormInstance) => async (dispatch: AppDispatch, getState: AppGetState, form: FormInstance) => {
  const currentForm = inputForm || form

  const { newServiceProvider } = getState()
  const { value } = newServiceProvider
  const { provider_cod_currency_target, provider_cod_currency_code, provider_cod_amount_target, provider_cod_amount } = currentForm.getFieldsValue()

  const codCurrencyConverted = value?.codCurrencyConverted

  const finalCodCurrencyCode = codCurrencyConverted ? provider_cod_currency_target : provider_cod_currency_code
  const finalCodAmount = codCurrencyConverted ? provider_cod_amount_target : provider_cod_amount

  return {
    codCurrencyCode: finalCodCurrencyCode,
    codAmount: finalCodAmount,
  }
}

/**
 * get deduct values
 */
export const getDeductValues = (inputForm?: FormInstance) => async (dispatch: AppDispatch, getState: AppGetState, form: FormInstance) => {
  const { newServiceProvider } = getState()
  const { props } = newServiceProvider
  const currentForm = inputForm || form
  const values = currentForm.getFieldsValue()
  const salesOrderCurrencyCode = props?.orderInfo?.salesOrderCurrencyCode
  const { deductedFreight, deductedFreightCurrencyCode } = props

  const { provider_cod_currency_code } = values

  const isDeductFreightEnable = salesOrderCurrencyCode && salesOrderCurrencyCode === provider_cod_currency_code

  if (!isDeductFreightEnable) return {}

  return {
    deductedFreight,
    deductedFreightCurrencyCode,
  }
}

export default configSlice.reducer
